import { FormattedMessage } from 'react-intl-next';
import React, { useCallback } from 'react';
import { Subscribe } from 'unstated';

import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { ErrorPageComponent } from '@confluence/not-found/entry-points/ErrorPageComponent.ts';
import { PageLoadEnd } from '@confluence/browser-metrics/entry-points/PageLoadEnd.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';
import { RESTRICTED_SPACE_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceSuccess } from '@confluence/experience-tracker/entry-points/component.ts';

import ConfluenceLogo from './ConfluenceLogo.svg';
import RestrictedContentIconDark from './RestrictedContentIcon-Dark.svg';
import RestrictedContentIcon from './RestrictedContentIcon-Light.svg';
import { INACCESSIBLE_PAGE_LOAD } from './perf.config.js';
import { SuperAdminGateway } from './SuperAdminGateway.js';
import { useButtonProps } from './useRestrictedSpaceScreenButtonProps.tsx';
import { useRequestSpaceAccessMutation } from './useRequestSpaceAccessMutation.tsx';
import type { ConfluenceJoinabilityType } from './request-license/useIsAuthenticatedUnlicensedWithDirectAccess.ts';

interface RestrictedSpaceScreenProps {
	contentId?: string;
	confluenceJoinability: ConfluenceJoinabilityType;
	identityLink: string;
}

export const RestrictedSpaceForLicensedUserComponent = ({
	flags,
	contentId,
	confluenceJoinability,
	identityLink,
}: WithFlagsProps & RestrictedSpaceScreenProps) => {
	const spaceKey = useSpaceKey() || '';

	const { isRequestAccessPending, updateSpaceAccessMutation } = useRequestSpaceAccessMutation({
		flags,
	});

	const { primary, secondary } = useButtonProps({
		confluenceJoinability,
		identityLink,
		updateSpaceAccessMutation,
		spaceKey,
		isRequestAccessPending,
	});

	const getErrorMessages = useCallback(() => {
		switch (confluenceJoinability) {
			case 'canJoinDirectly':
				return {
					errorTitle: (
						<FormattedMessage
							id="no-permission.restricted.space.title.authenticated.unlicensed"
							description="Title displayed for a restricted space for an authenticated user without a license"
							defaultMessage="You need full Confluence access to view this space"
						/>
					),
					errorMessage: (
						<FormattedMessage
							id="no-permission.restricted.space.message.authenticated.unlicensed"
							description="Message displayed for restricted space for an authenticated user without a license"
							// eslint-disable-next-line @atlassian/i18n/no-multiple-whitespaces
							defaultMessage="Your access to Confluence is currently limited. To view this space, you’ll need submit a request for full access.
			  If this space is restricted, you may also need to submit request for space access."
						/>
					),
					errorImage: RestrictedContentIcon,
					errorImageDark: RestrictedContentIconDark,
				};
			case 'licensingInProgress':
				return {
					errorTitle: (
						<FormattedMessage
							id="no-permission.restricted.space.title.authenticated.licensing.in.progress"
							description="Title displayed for a restricted space for an authenticated user with licensing in progress"
							defaultMessage="Your Confluence access is still processing"
						/>
					),
					errorMessage: null,
					errorImage: ConfluenceLogo,
					errorImageDark: ConfluenceLogo,
				};
			default:
				return {
					errorTitle: (
						<FormattedMessage
							id="no-permission.restricted.space.licensed-user.request-access.title"
							defaultMessage="You don’t have access to this space"
							description="title text for the restricted space screen for licensed users"
						/>
					),
					errorMessage: null,
					errorImage: RestrictedContentIcon,
					errorImageDark: RestrictedContentIconDark,
				};
		}
	}, [confluenceJoinability]);

	const { errorTitle, errorMessage, errorImage, errorImageDark } = getErrorMessages();

	return (
		<>
			<ErrorPageComponent
				errorImage={errorImage}
				errorImageDark={errorImageDark}
				errorTitle={errorTitle}
				errorMessage={errorMessage}
				primaryButtonProps={primary}
				secondaryButtonProps={secondary}
			>
				<SuperAdminGateway type="space" contentId={contentId} />
			</ErrorPageComponent>

			<ExperienceSuccess name={RESTRICTED_SPACE_EXPERIENCE} />
			<PageLoadEnd
				metric={INACCESSIBLE_PAGE_LOAD}
				customData={{
					inaccessiblePageType: 'RESTRICTED_SPACE',
				}}
			/>
		</>
	);
};

export const RestrictedSpaceScreen = (props: RestrictedSpaceScreenProps) => (
	<ErrorBoundary attribution={Attribution.PERMISSIONS_EXPERIENCE}>
		<Subscribe to={[FlagsStateContainer]}>
			{(flags: FlagsStateContainer) => (
				<RestrictedSpaceForLicensedUserComponent flags={flags} {...props} />
			)}
		</Subscribe>
	</ErrorBoundary>
);
